import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, RefreshCw, Lock, CheckCircle2, XCircle, KeyRound, AlertTriangle, Unlock,
  Send, Globe, Plus, Trash2, BarChart3, MapPin, X, Users, Activity, LogOut, Clock, ChevronDown, ChevronRight,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader } from "../components/common";
import ThreatMap from "../components/ThreatMap";
import { overlayCloseHandlers } from "../lib/overlayClose";
import { useAuth } from "../auth";

function Kpi({ icon: Icon, label, value, tone = "navy", testid }) {
  const tones = {
    navy: "border-l-navy text-navy",
    red: "border-l-red-500 text-red-600",
    amber: "border-l-amber-500 text-amber-600",
    green: "border-l-emerald-500 text-emerald-600",
  };
  const cls = tones[tone] || tones.navy;
  return (
    <div data-testid={testid} className={`bg-white border border-tint border-l-4 ${cls.split(" ")[0]} rounded-xl p-5`}>
      <div className="flex items-start justify-between">
        <span className="text-muted2 text-[11px] uppercase tracking-[0.14em] font-semibold">{label}</span>
        <Icon size={18} className={cls.split(" ")[1]} />
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight text-navy">{value}</div>
    </div>
  );
}

const FILTERS = [
  { key: "all", label: "Todas" },
  { key: "false", label: "Falhas" },
  { key: "true", label: "Sucessos" },
];

function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return "🌐";
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
}

export default function Security() {
  const { can } = useAuth();
  const isSuper = can("*");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("false");
  const [attemptsPage, setAttemptsPage] = useState(0);
  const [activeSessions, setActiveSessions] = useState({ sessions: [], count: 0, minutes: 15 });
  const [activeWindow, setActiveWindow] = useState(15);
  const [knownIps, setKnownIps] = useState({ users: [], total_ips: 0, ttl_days: 30 });
  const [knownExpanded, setKnownExpanded] = useState({});
  const [auditData, setAuditData] = useState({ events: [], total: 0, skip: 0, limit: 25, tipos: [] });
  const [auditPage, setAuditPage] = useState(0);
  const [auditFilterTipo, setAuditFilterTipo] = useState("");
  const [auditFilterEmail, setAuditFilterEmail] = useState("");
  const AUDIT_PAGE_SIZE = 25;
  const [historyData, setHistoryData] = useState({ history: [], total: 0, razoes: [] });
  const [historyPage, setHistoryPage] = useState(0);
  const [historyFilterRazao, setHistoryFilterRazao] = useState("");
  const [historyFilterEmail, setHistoryFilterEmail] = useState("");
  const HISTORY_PAGE_SIZE = 25;
  const [secStatus, setSecStatus] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [unblock, setUnblock] = useState(null); // {email, ip, motivo} | null
  const [unblocking, setUnblocking] = useState(false);
  const [hourly, setHourly] = useState([]);
  const [tg, setTg] = useState(null);
  const [allowlist, setAllowlist] = useState([]);
  const [newIp, setNewIp] = useState({ ip: "", nota: "" });
  const [geo, setGeo] = useState(null);        // {hours, totals, buckets}
  const [geoHours, setGeoHours] = useState(24);
  const [geoView, setGeoView] = useState("map");     // map | bars
  const [geoMetric, setGeoMetric] = useState("failures");
  const [country, setCountry] = useState(null); // {enabled, allowed_countries, block_unauthorized, block_after_fails, block_duration_min}
  const [countryNew, setCountryNew] = useState("");
  const [countryEvents, setCountryEvents] = useState([]);
  const [blocks, setBlocks] = useState(null);   // {hours,total_events,blocks:[]}

  const ATTEMPTS_PAGE_SIZE = 25;

  const load = useCallback(async (page = attemptsPage) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: String(ATTEMPTS_PAGE_SIZE), skip: String(page * ATTEMPTS_PAGE_SIZE) });
      if (filter !== "all") params.set("success", filter);
      const { data } = await api.get(`/super-admin/login-attempts?${params.toString()}`);
      setData(data);
      setAttemptsPage(page);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sem permissao para ver este painel.");
    } finally { setLoading(false); }
  }, [filter, attemptsPage]);

  // Reset page ao mudar filtro
  useEffect(() => { load(0); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    api.get("/super-admin/security-status").then((r) => setSecStatus(r.data)).catch(() => {});
    api.get("/super-admin/login-attempts/hourly?hours=24").then((r) => setHourly(r.data.buckets || [])).catch(() => {});
    api.get("/security/telegram").then((r) => { setTg(r.data); }).catch(() => {});
    api.get("/security/ip-allowlist").then((r) => setAllowlist(r.data.ips || [])).catch(() => {});
    api.get("/security/country-anomaly/config").then((r) => setCountry(r.data)).catch(() => {});
    api.get("/security/country-anomaly/events?limit=15").then((r) => setCountryEvents(r.data.events || [])).catch(() => {});
    api.get("/security/blocks?hours=24&limit=30").then((r) => setBlocks(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isSuper) return;
    api.get(`/super-admin/login-attempts/geo?hours=${geoHours}`).then((r) => setGeo(r.data)).catch(() => {});
  }, [geoHours, isSuper]);

  // Sessoes activas em tempo real - polling a cada 10s
  useEffect(() => {
    if (!isSuper) return;
    let alive = true;
    const fetchActive = () => {
      api.get(`/security/active-sessions?minutes=${activeWindow}`)
        .then((r) => { if (alive) setActiveSessions(r.data || { sessions: [], count: 0, minutes: activeWindow }); })
        .catch(() => {});
    };
    fetchActive();
    const it = setInterval(fetchActive, 10000);
    return () => { alive = false; clearInterval(it); };
  }, [activeWindow, isSuper]);

  const revokeSession = async (s) => {
    if (!s?.user_id) return;
    if (!window.confirm(`Terminar sessao de ${s.email || s.nome}?\n\nOs tokens actuais deixam de ser aceites; o utilizador precisa de iniciar sessao novamente.`)) return;
    try {
      const { data } = await api.post(`/security/active-sessions/${s.user_id}/revoke`);
      toast.success(`Sessao de ${s.email || s.nome} revogada (${data?.sessoes_removidas || 0} sessoes).`);
      setActiveSessions((prev) => ({
        ...prev,
        sessions: (prev.sessions || []).filter((x) => x.user_id !== s.user_id),
        count: Math.max(0, (prev.count || 0) - (data?.sessoes_removidas || 1)),
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nao foi possivel revogar a sessao.");
    }
  };

  const trustIp = async (userId, ip, emailLabel) => {
    if (!userId || !ip) return;
    if (!window.confirm(`Confiar no IP ${ip} para ${emailLabel || userId}?\n\nDurante ${knownIps.ttl_days || 30} dias nao serao enviados novos alertas Telegram para este IP.`)) return;
    try {
      await api.post("/security/known-ips/trust", { user_id: userId, ip });
      toast.success(`IP ${ip} confiavel para ${emailLabel || userId}.`);
      loadKnownIps();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nao foi possivel confiar no IP.");
    }
  };

  const loadKnownIps = async () => {
    try {
      const { data } = await api.get("/security/known-ips");
      setKnownIps(data || { users: [], total_ips: 0, ttl_days: 30 });
    } catch (e) { /* silent */ }
  };

  useEffect(() => { if (isSuper) loadKnownIps(); }, [isSuper]);

  const loadAudit = async (page = auditPage, tipo = auditFilterTipo, email = auditFilterEmail) => {
    try {
      const params = new URLSearchParams({ limit: String(AUDIT_PAGE_SIZE), skip: String(page * AUDIT_PAGE_SIZE) });
      if (tipo) params.set("tipo", tipo);
      if (email) params.set("email", email);
      const { data } = await api.get(`/security/events?${params.toString()}`);
      setAuditData(data || { events: [], total: 0, skip: 0, limit: AUDIT_PAGE_SIZE, tipos: [] });
      setAuditPage(page);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { if (isSuper) loadAudit(0, auditFilterTipo, auditFilterEmail); /* eslint-disable-next-line */ }, [isSuper, auditFilterTipo]);

  const loadHistory = async (page = historyPage, razao = historyFilterRazao, email = historyFilterEmail) => {
    try {
      const params = new URLSearchParams({ limit: String(HISTORY_PAGE_SIZE), skip: String(page * HISTORY_PAGE_SIZE) });
      if (razao) params.set("razao", razao);
      if (email) params.set("email", email);
      const { data } = await api.get(`/security/session-history?${params.toString()}`);
      setHistoryData(data || { history: [], total: 0, razoes: [] });
      setHistoryPage(page);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { if (isSuper) loadHistory(0, historyFilterRazao, historyFilterEmail); /* eslint-disable-next-line */ }, [isSuper, historyFilterRazao]);

  const removeKnownIp = async (uid, ip) => {
    if (!window.confirm(`Remover o IP ${ip}?\n\nNa proxima sessao vai gerar alerta de novo IP para este utilizador.`)) return;
    try {
      await api.delete("/security/known-ips", { data: { user_id: uid, ip } });
      toast.success("IP removido dos locais confiaveis.");
      loadKnownIps();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetKnownIpsForUser = async (uid, email, total) => {
    if (!window.confirm(`Remover TODOS os ${total} locais confiaveis de ${email}?\n\nCada novo login sera tratado como sessao nova.`)) return;
    try {
      const { data } = await api.delete(`/security/known-ips/${uid}`);
      toast.success(`${data.removidos} locais removidos para ${email}.`);
      loadKnownIps();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveCountry = async (next) => {
    try {
      const payload = {
        enabled: next.enabled ?? country?.enabled ?? true,
        allowed_countries: next.allowed_countries ?? country?.allowed_countries ?? [],
        block_unauthorized: next.block_unauthorized ?? country?.block_unauthorized ?? false,
        block_after_fails: next.block_after_fails ?? country?.block_after_fails ?? 3,
        block_duration_min: next.block_duration_min ?? country?.block_duration_min ?? 60,
      };
      const { data } = await api.put("/security/country-anomaly/config", payload);
      setCountry(data);
      toast.success("Config guardada.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addCountry = () => {
    const code = (countryNew || "").trim().toUpperCase();
    if (code.length !== 2) return toast.error("Codigo ISO-2 (ex: PT).");
    const next = { allowed_countries: [...new Set([...(country?.allowed_countries || []), code])] };
    setCountryNew("");
    saveCountry(next);
  };
  const removeCountry = (code) => {
    saveCountry({ allowed_countries: (country?.allowed_countries || []).filter((c) => c !== code) });
  };
  const toggleCountry = (v) => { saveCountry({ enabled: v }); };
  const toggleBlock = (v) => { saveCountry({ block_unauthorized: v }); };

  const addAllow = async () => {
    if (!newIp.ip.trim()) { toast.error("Indique um IP."); return; }
    try {
      const { data } = await api.post("/security/ip-allowlist", newIp);
      setAllowlist(data.ips || []);
      setNewIp({ ip: "", nota: "" });
      toast.success("IP adicionado a lista de confianca.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delAllow = async (ip) => {
    try {
      await api.delete(`/security/ip-allowlist/${encodeURIComponent(ip)}`);
      setAllowlist((a) => a.filter((x) => x.ip !== ip));
      toast.success("IP removido.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const rotate = async () => {
    if (!window.confirm("Rodar o segredo JWT invalida TODAS as sessoes ativas, incluindo a sua. Continuar?")) return;
    setRotating(true);
    try {
      await api.post("/super-admin/rotate-jwt-secret", { confirm: true });
      toast.success("Segredo JWT rodado. Vai precisar de iniciar sessao novamente.");
      setTimeout(() => { window.location.href = "/login"; }, 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setRotating(false); }
  };

  const doUnblock = async () => {
    if (!unblock?.email && !unblock?.ip) { toast.error("Indique um email ou IP."); return; }
    setUnblocking(true);
    try {
      const { data } = await api.post("/super-admin/login-attempts/reset", {
        email: unblock.email || null, ip: unblock.ip || null, motivo: unblock.motivo || "",
      });
      toast.success(`Desbloqueado: ${data.deleted_count} tentativa(s) falhada(s) removida(s).`);
      setUnblock(null);
      load(attemptsPage);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUnblocking(false); }
  };

  if (!isSuper) {
    return (
      <div>
        <PageHeader overline="Administracao" title="Seguranca" />
        <div className="bg-white border border-tint rounded-xl p-8 text-center text-muted2" data-testid="security-no-access">
          <Lock size={28} className="mx-auto mb-3 text-slate-400" />
          Este painel esta reservado a super administradores.
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const attempts = data?.attempts || [];
  const attemptsTotal = data?.total || 0;
  const attemptsMaxPage = Math.max(0, Math.ceil(attemptsTotal / ATTEMPTS_PAGE_SIZE) - 1);
  const attemptsFrom = attempts.length ? attemptsPage * ATTEMPTS_PAGE_SIZE + 1 : 0;
  const attemptsTo = attemptsPage * ATTEMPTS_PAGE_SIZE + attempts.length;

  return (
    <div>
      <PageHeader
        overline="Administracao"
        title="Seguranca de acesso"
        subtitle="Monitorizacao de tentativas de login e bloqueio automatico contra ataques de forca bruta."
        action={
          <button onClick={() => load(attemptsPage)} disabled={loading} data-testid="security-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-[var(--blue-hover)] disabled:opacity-50">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
        }
      />

      {/* Sessoes activas em tempo real */}
      <div className="mb-6 border border-tint bg-white rounded-xl overflow-hidden" data-testid="active-sessions-panel">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-tint bg-gradient-to-r from-emerald-50 to-white">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <div>
              <div className="text-sm font-semibold text-navy inline-flex items-center gap-1.5">
                <Activity size={14} className="text-emerald-600" /> Utilizadores online agora
              </div>
              <div className="text-[11px] text-muted2">
                <b className="text-navy">{activeSessions.count || 0}</b> sessoes activas nos ultimos {activeSessions.minutes || activeWindow} min. Actualiza a cada 10 segundos.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1" data-testid="active-window">
            {[5, 15, 30, 60].map((m) => (
              <button key={m} onClick={() => setActiveWindow(m)} data-testid={`active-window-${m}`}
                className={`text-[11px] px-2 py-1 rounded border ${activeWindow === m ? "bg-navy text-white border-navy" : "border-tint text-slate-600 hover:bg-tint"}`}>
                {m} min
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-muted2 text-[11px] uppercase tracking-wide">
                <th className="text-left px-4 py-2 font-semibold">Utilizador</th>
                <th className="text-left px-4 py-2 font-semibold">Email</th>
                <th className="text-left px-4 py-2 font-semibold">IP</th>
                <th className="text-left px-4 py-2 font-semibold">Localizacao</th>
                <th className="text-left px-4 py-2 font-semibold hidden md:table-cell">Papel</th>
                <th className="text-left px-4 py-2 font-semibold hidden lg:table-cell">Ultimo endpoint</th>
                <th className="text-right px-4 py-2 font-semibold">Ha</th>
                <th className="text-right px-4 py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {(activeSessions.sessions || []).length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-muted2 text-[13px]">Sem sessoes activas no periodo.</td></tr>
              )}
              {(activeSessions.sessions || []).map((s, i) => {
                const last = s.last_seen ? new Date(s.last_seen) : null;
                const secs = last ? Math.max(0, Math.round((Date.now() - last.getTime()) / 1000)) : 0;
                const rel = secs < 60 ? `${secs}s` : secs < 3600 ? `${Math.round(secs / 60)}min` : `${Math.round(secs / 3600)}h`;
                const fresh = secs < 60;
                return (
                  <tr key={s.user_id || i} data-testid={`active-session-row-${i}`} className="border-t border-tint hover:bg-slate-50">
                    <td className="px-4 py-2 text-navy font-medium">
                      <span className="inline-flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${fresh ? "bg-emerald-500" : "bg-amber-400"}`} title={fresh ? "Activo agora" : "Inactivo recente"} />
                        {s.nome || s.email || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-700 text-[12px]">{s.email || "-"}</td>
                    <td className="px-4 py-2 font-mono text-[12px] text-navy">{s.ip || <span className="text-slate-400">-</span>}</td>
                    <td className="px-4 py-2 text-sm text-navy">
                      {s.country ? (
                        <span className="inline-flex items-center gap-1">
                          <span>{flagEmoji(s.country_code)}</span>
                          <span className="truncate max-w-[140px]" title={s.country}>{s.country}</span>
                        </span>
                      ) : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">
                        {(s.roles || []).join(", ") || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-[11px] text-slate-500 truncate max-w-[260px] hidden lg:table-cell" title={s.last_path}>{s.last_path || "-"}</td>
                    <td className="px-4 py-2 text-right text-[11px] text-slate-500 whitespace-nowrap">{rel}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => trustIp(s.user_id, s.ip, s.email)}
                          data-testid={`trust-session-${i}`}
                          title="Confiar neste IP para este utilizador (30 dias)"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-white hover:bg-emerald-600 border border-emerald-200 hover:border-emerald-600 rounded px-2 py-1 transition-colors">
                          <ShieldCheck size={11} /> Confiar
                        </button>
                        <button
                          onClick={() => revokeSession(s)}
                          data-testid={`revoke-session-${i}`}
                          title="Terminar sessao (forca logout)"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 rounded px-2 py-1 transition-colors">
                          <LogOut size={11} /> Terminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Locais confiaveis (Iter103) - user_known_ips com TTL 30 dias */}
      <div className="mb-6 border border-tint bg-white rounded-xl overflow-hidden" data-testid="known-ips-panel">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-tint bg-gradient-to-r from-sky-50 to-white">
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-sky-600" />
            <div>
              <div className="text-sm font-semibold text-navy">Locais confiaveis</div>
              <div className="text-[11px] text-muted2">
                <b className="text-navy">{knownIps.total_ips || 0}</b> IPs de <b className="text-navy">{(knownIps.users || []).length}</b> utilizadores.
                Reconfirmacao automatica a cada <b>{knownIps.ttl_days || 30}</b> dias (nova alerta Telegram se o IP nao reactivar antes).
              </div>
            </div>
          </div>
          <button onClick={loadKnownIps} data-testid="known-ips-refresh"
            className="text-[11px] font-semibold text-slate-600 hover:text-navy inline-flex items-center gap-1 px-2 py-1 rounded border border-tint hover:bg-tint">
            <RefreshCw size={11} /> Atualizar
          </button>
        </div>
        <div className="divide-y divide-tint">
          {(knownIps.users || []).length === 0 && (
            <div className="px-4 py-6 text-center text-muted2 text-[13px]">Sem locais confiaveis registados.</div>
          )}
          {(knownIps.users || []).map((u) => {
            const expanded = !!knownExpanded[u.user_id];
            return (
              <div key={u.user_id} data-testid={`known-user-${u.user_id}`}>
                <div className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setKnownExpanded((s) => ({ ...s, [u.user_id]: !expanded }))}>
                  <span className="text-navy">
                    {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-navy truncate">{u.email || u.user_id}</div>
                    <div className="text-[11px] text-muted2">
                      <b className="text-navy">{u.activos}</b> activos - <b>{u.expirados}</b> expirados - {(u.paises || []).map((cc) => `${flagEmoji(cc)} ${cc}`).join(" ") || "sem geo"}
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); resetKnownIpsForUser(u.user_id, u.email || u.user_id, u.total); }}
                    data-testid={`known-reset-${u.user_id}`}
                    title="Remover todos os locais confiaveis"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-red-600 border border-tint hover:border-red-200 rounded px-2 py-1">
                    <Trash2 size={11} /> Reset
                  </button>
                </div>
                {expanded && (
                  <div className="bg-slate-50 border-t border-tint overflow-x-auto">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="text-muted2 text-[10px] uppercase tracking-wider">
                          <th className="text-left px-4 py-2 font-semibold">IP</th>
                          <th className="text-left px-4 py-2 font-semibold">Localizacao</th>
                          <th className="text-left px-4 py-2 font-semibold">Primeiro visto</th>
                          <th className="text-left px-4 py-2 font-semibold">Ultima actividade</th>
                          <th className="text-left px-4 py-2 font-semibold">Expira</th>
                          <th className="text-right px-4 py-2 font-semibold"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(u.ips || []).map((ip, k) => {
                          const days = ip.days_left ?? 0;
                          const badgeCls = ip.expired ? "bg-red-100 text-red-700 border-red-200"
                            : days <= 5 ? "bg-amber-100 text-amber-700 border-amber-200"
                            : "bg-emerald-100 text-emerald-700 border-emerald-200";
                          return (
                            <tr key={ip.ip + k} data-testid={`known-row-${u.user_id}-${k}`} className="border-t border-tint hover:bg-white">
                              <td className="px-4 py-2 font-mono text-navy">{ip.ip}</td>
                              <td className="px-4 py-2">
                                {ip.country ? <span className="inline-flex items-center gap-1"><span>{flagEmoji(ip.country_code)}</span><span>{ip.country}</span></span> : <span className="text-slate-400">-</span>}
                              </td>
                              <td className="px-4 py-2 text-slate-500">{ip.first_seen ? new Date(ip.first_seen).toLocaleDateString("pt-PT") : "-"}</td>
                              <td className="px-4 py-2 text-slate-500">{ip.last_seen ? new Date(ip.last_seen).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "-"}</td>
                              <td className="px-4 py-2">
                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${badgeCls}`}>
                                  {ip.expired ? "Expirado" : `${days}d restantes`}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-right whitespace-nowrap">
                                <div className="inline-flex items-center gap-1">
                                  {(ip.expired || (ip.days_left ?? 0) <= 5) && (
                                    <button onClick={() => trustIp(u.user_id, ip.ip, u.email || u.user_id)}
                                      data-testid={`known-trust-${u.user_id}-${k}`}
                                      title="Reconfirmar como confiavel (renova TTL 30 dias)"
                                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-white hover:bg-emerald-600 border border-emerald-200 hover:border-emerald-600 rounded px-2 py-1 transition-colors">
                                      <ShieldCheck size={11} /> Renovar
                                    </button>
                                  )}
                                  <button onClick={() => removeKnownIp(u.user_id, ip.ip)}
                                    data-testid={`known-remove-${u.user_id}-${k}`}
                                    title="Remover deste utilizador"
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 rounded px-2 py-1 transition-colors">
                                    <X size={11} /> Remover
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Historico de sessoes fechadas (Iter105) */}
      <div className="mb-6 border border-tint bg-white rounded-xl overflow-hidden" data-testid="history-panel">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-tint bg-gradient-to-r from-amber-50 to-white">
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-amber-600" />
            <div>
              <div className="text-sm font-semibold text-navy">Historico de sessoes fechadas</div>
              <div className="text-[11px] text-muted2">
                Snapshots imutaveis de sessoes revogadas ou rejeitadas. Total: <b className="text-navy">{historyData.total || 0}</b>.
              </div>
            </div>
          </div>
          <button onClick={() => loadHistory(historyPage, historyFilterRazao, historyFilterEmail)} data-testid="history-refresh"
            className="text-[11px] font-semibold text-slate-600 hover:text-navy inline-flex items-center gap-1 px-2 py-1 rounded border border-tint hover:bg-tint">
            <RefreshCw size={11} /> Atualizar
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-tint bg-slate-50">
          <select value={historyFilterRazao} onChange={(e) => setHistoryFilterRazao(e.target.value)}
            data-testid="history-filter-razao"
            className="text-[11px] border border-tint bg-white rounded px-2 py-1">
            <option value="">Todas as razoes</option>
            {(historyData.razoes || []).map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
          <input value={historyFilterEmail} onChange={(e) => setHistoryFilterEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadHistory(0, historyFilterRazao, historyFilterEmail)}
            data-testid="history-filter-email"
            placeholder="Filtrar por email..."
            className="flex-1 min-w-[180px] text-[11px] border border-tint bg-white rounded px-2 py-1" />
          <button onClick={() => loadHistory(0, historyFilterRazao, historyFilterEmail)} data-testid="history-search"
            className="text-[11px] font-semibold text-white bg-navy hover:bg-navy/90 rounded px-2.5 py-1">Filtrar</button>
          {(historyFilterEmail || historyFilterRazao) && (
            <button onClick={() => { setHistoryFilterEmail(""); setHistoryFilterRazao(""); loadHistory(0, "", ""); }}
              data-testid="history-clear"
              className="text-[11px] font-semibold text-slate-500 hover:text-red-600">Limpar</button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-slate-50 text-muted2 text-[10px] uppercase tracking-wider">
                <th className="text-left px-4 py-2 font-semibold">Utilizador</th>
                <th className="text-left px-4 py-2 font-semibold">IP</th>
                <th className="text-left px-4 py-2 font-semibold">Localizacao</th>
                <th className="text-left px-4 py-2 font-semibold hidden md:table-cell">User-Agent</th>
                <th className="text-left px-4 py-2 font-semibold">Razao</th>
                <th className="text-left px-4 py-2 font-semibold hidden md:table-cell">Por</th>
                <th className="text-right px-4 py-2 font-semibold">Fechada</th>
                <th className="text-right px-4 py-2 font-semibold hidden lg:table-cell">Duracao</th>
              </tr>
            </thead>
            <tbody>
              {(historyData.history || []).length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-muted2 text-[13px]">Sem sessoes fechadas registadas.</td></tr>
              )}
              {(historyData.history || []).map((h, i) => {
                const razaoCls = {
                  admin_revoke: "bg-red-100 text-red-700 border-red-200",
                  telegram_reject: "bg-red-100 text-red-700 border-red-200",
                  session_revoked_middleware: "bg-amber-100 text-amber-700 border-amber-200",
                }[h.razao] || "bg-slate-100 text-slate-700 border-slate-200";
                let duracao = "-";
                if (h.first_seen && h.closed_at) {
                  const dur = new Date(h.closed_at).getTime() - new Date(h.first_seen).getTime();
                  const mins = Math.round(dur / 60000);
                  if (mins >= 60) duracao = `${Math.floor(mins / 60)}h ${mins % 60}min`;
                  else if (mins > 0) duracao = `${mins}min`;
                  else duracao = "<1min";
                }
                return (
                  <tr key={i} data-testid={`history-row-${i}`} className="border-t border-tint hover:bg-slate-50">
                    <td className="px-4 py-2 text-navy font-medium">
                      <div className="truncate max-w-[200px]">{h.email || h.user_id || "-"}</div>
                      <div className="text-[10px] text-slate-400">{(h.roles || []).join(", ")}</div>
                    </td>
                    <td className="px-4 py-2 font-mono text-navy">{h.ip || "-"}</td>
                    <td className="px-4 py-2">
                      {h.country ? (
                        <span className="inline-flex items-center gap-1">
                          <span>{flagEmoji(h.country_code)}</span>
                          <span className="truncate max-w-[120px]" title={h.country}>{h.country}</span>
                        </span>
                      ) : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-2 text-[11px] text-slate-500 hidden md:table-cell truncate max-w-[220px]" title={h.user_agent}>{h.user_agent || "-"}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${razaoCls}`}>
                        {(h.razao || "").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-[11px] text-slate-500 hidden md:table-cell truncate max-w-[180px]" title={h.closed_by}>{h.closed_by || "-"}</td>
                    <td className="px-4 py-2 text-right text-[11px] text-slate-500 whitespace-nowrap">
                      {h.closed_at ? new Date(h.closed_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "medium" }) : "-"}
                    </td>
                    <td className="px-4 py-2 text-right text-[11px] text-slate-500 hidden lg:table-cell">{duracao}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {historyData.total > HISTORY_PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-tint bg-slate-50">
            <div className="text-[11px] text-muted2">
              A mostrar {historyPage * HISTORY_PAGE_SIZE + 1}-{historyPage * HISTORY_PAGE_SIZE + (historyData.history || []).length} de {historyData.total}
            </div>
            <div className="flex items-center gap-1" data-testid="history-pager">
              <button
                onClick={() => historyPage > 0 && loadHistory(historyPage - 1, historyFilterRazao, historyFilterEmail)}
                disabled={historyPage <= 0}
                data-testid="history-pager-prev"
                className={`text-[11px] px-2 py-1 rounded border ${historyPage > 0 ? "border-tint text-navy hover:bg-slate-100" : "border-tint text-slate-300 cursor-not-allowed"}`}>Anteriores</button>
              <span className="text-[11px] text-slate-500 px-1.5">{historyPage + 1} / {Math.max(1, Math.ceil(historyData.total / HISTORY_PAGE_SIZE))}</span>
              <button
                onClick={() => (historyPage + 1) * HISTORY_PAGE_SIZE < historyData.total && loadHistory(historyPage + 1, historyFilterRazao, historyFilterEmail)}
                disabled={(historyPage + 1) * HISTORY_PAGE_SIZE >= historyData.total}
                data-testid="history-pager-next"
                className={`text-[11px] px-2 py-1 rounded border ${(historyPage + 1) * HISTORY_PAGE_SIZE < historyData.total ? "border-tint text-navy hover:bg-slate-100" : "border-tint text-slate-300 cursor-not-allowed"}`}>Seguintes</button>
            </div>
          </div>
        )}
      </div>

      {/* Auditoria de eventos (Iter104) - security_events */}
      <div className="mb-6 border border-tint bg-white rounded-xl overflow-hidden" data-testid="audit-panel">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-tint bg-gradient-to-r from-slate-100 to-white">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-slate-600" />
            <div>
              <div className="text-sm font-semibold text-navy">Auditoria de seguranca</div>
              <div className="text-[11px] text-muted2">
                Registo imutavel de revogacoes, confirmacoes, remocoes e cleanups. Total: <b className="text-navy">{auditData.total || 0}</b>.
              </div>
            </div>
          </div>
          <button onClick={() => loadAudit(auditPage, auditFilterTipo, auditFilterEmail)} data-testid="audit-refresh"
            className="text-[11px] font-semibold text-slate-600 hover:text-navy inline-flex items-center gap-1 px-2 py-1 rounded border border-tint hover:bg-tint">
            <RefreshCw size={11} /> Atualizar
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-tint bg-slate-50">
          <select value={auditFilterTipo} onChange={(e) => setAuditFilterTipo(e.target.value)}
            data-testid="audit-filter-tipo"
            className="text-[11px] border border-tint bg-white rounded px-2 py-1">
            <option value="">Todos os tipos</option>
            {(auditData.tipos || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <input value={auditFilterEmail} onChange={(e) => setAuditFilterEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadAudit(0, auditFilterTipo, auditFilterEmail)}
            data-testid="audit-filter-email"
            placeholder="Filtrar por email..."
            className="flex-1 min-w-[180px] text-[11px] border border-tint bg-white rounded px-2 py-1" />
          <button onClick={() => loadAudit(0, auditFilterTipo, auditFilterEmail)} data-testid="audit-search"
            className="text-[11px] font-semibold text-white bg-navy hover:bg-navy/90 rounded px-2.5 py-1">Filtrar</button>
          {(auditFilterEmail || auditFilterTipo) && (
            <button onClick={() => { setAuditFilterEmail(""); setAuditFilterTipo(""); loadAudit(0, "", ""); }}
              data-testid="audit-clear"
              className="text-[11px] font-semibold text-slate-500 hover:text-red-600">Limpar</button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-slate-50 text-muted2 text-[10px] uppercase tracking-wider">
                <th className="text-left px-4 py-2 font-semibold">Tipo</th>
                <th className="text-left px-4 py-2 font-semibold">Utilizador</th>
                <th className="text-left px-4 py-2 font-semibold">Detalhes</th>
                <th className="text-left px-4 py-2 font-semibold hidden md:table-cell">Por</th>
                <th className="text-right px-4 py-2 font-semibold">Quando</th>
              </tr>
            </thead>
            <tbody>
              {(auditData.events || []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted2 text-[13px]">Sem eventos registados.</td></tr>
              )}
              {(auditData.events || []).map((ev, i) => {
                const tipoCls = {
                  sessao_suspeita: "bg-amber-100 text-amber-700 border-amber-200",
                  sessao_revogada: "bg-red-100 text-red-700 border-red-200",
                  sessao_rejeitada_telegram: "bg-red-100 text-red-700 border-red-200",
                  ip_confirmado_telegram: "bg-emerald-100 text-emerald-700 border-emerald-200",
                  ip_confiavel_removido: "bg-slate-100 text-slate-700 border-slate-200",
                  ip_confiavel_reset: "bg-slate-100 text-slate-700 border-slate-200",
                  ip_confiavel_cleanup: "bg-sky-100 text-sky-700 border-sky-200",
                }[ev.tipo] || "bg-slate-100 text-slate-600 border-slate-200";
                const details = [
                  ev.reason,
                  ev.ip,
                  ev.country && `${flagEmoji(ev.country_code)} ${ev.country}`,
                  ev.count != null && `${ev.count} entradas`,
                  ev.removidos != null && `${ev.removidos} removidos`,
                  ev.cutoff && `cutoff ${new Date(ev.cutoff).toLocaleDateString("pt-PT")}`,
                ].filter(Boolean).join(" - ");
                const actor = ev.revoked_by || ev.removed_by || (ev.tipo === "ip_confiavel_cleanup" ? "sistema (job)" : "");
                return (
                  <tr key={i} data-testid={`audit-row-${i}`} className="border-t border-tint hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${tipoCls}`}>
                        {(ev.tipo || "").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-navy font-medium">{ev.email || ev.user_id || "-"}</td>
                    <td className="px-4 py-2 text-slate-600 truncate max-w-[400px]" title={details}>{details || "-"}</td>
                    <td className="px-4 py-2 text-[11px] text-slate-500 hidden md:table-cell">{actor || "-"}</td>
                    <td className="px-4 py-2 text-right text-[11px] text-slate-500 whitespace-nowrap">
                      {ev.em ? new Date(ev.em).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "medium" }) : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pager */}
        {auditData.total > AUDIT_PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-tint bg-slate-50">
            <div className="text-[11px] text-muted2">
              A mostrar {auditPage * AUDIT_PAGE_SIZE + 1}-{auditPage * AUDIT_PAGE_SIZE + (auditData.events || []).length} de {auditData.total}
            </div>
            <div className="flex items-center gap-1" data-testid="audit-pager">
              <button
                onClick={() => auditPage > 0 && loadAudit(auditPage - 1, auditFilterTipo, auditFilterEmail)}
                disabled={auditPage <= 0}
                data-testid="audit-pager-prev"
                className={`text-[11px] px-2 py-1 rounded border ${auditPage > 0 ? "border-tint text-navy hover:bg-slate-100" : "border-tint text-slate-300 cursor-not-allowed"}`}>Anteriores</button>
              <span className="text-[11px] text-slate-500 px-1.5">{auditPage + 1} / {Math.max(1, Math.ceil(auditData.total / AUDIT_PAGE_SIZE))}</span>
              <button
                onClick={() => (auditPage + 1) * AUDIT_PAGE_SIZE < auditData.total && loadAudit(auditPage + 1, auditFilterTipo, auditFilterEmail)}
                disabled={(auditPage + 1) * AUDIT_PAGE_SIZE >= auditData.total}
                data-testid="audit-pager-next"
                className={`text-[11px] px-2 py-1 rounded border ${(auditPage + 1) * AUDIT_PAGE_SIZE < auditData.total ? "border-tint text-navy hover:bg-slate-100" : "border-tint text-slate-300 cursor-not-allowed"}`}>Seguintes</button>
            </div>
          </div>
        )}
      </div>

      {/* Politica de protecao */}
      <div className="mb-6 flex items-start gap-3 border border-blue-200 bg-blue-50 rounded-lg p-4" data-testid="security-policy">
        <ShieldCheck className="text-blue mt-0.5 shrink-0" size={20} />
        <div className="text-sm text-navy">
          <b>Bloqueio automatico ativo:</b> apos 5 tentativas falhadas por email OU por IP numa janela de 15 minutos,
          novos pedidos de login sao recusados temporariamente (HTTP 429). O registo tem retencao de 90 dias.
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="security-kpis">
        <Kpi icon={AlertTriangle} label="Falhas (15 min)" value={stats.failures_15m ?? 0} tone={stats.failures_15m >= 5 ? "red" : "navy"} testid="kpi-fails-15m" />
        <Kpi icon={XCircle} label="Falhas (24 h)" value={stats.failures_24h ?? 0} tone={stats.failures_24h > 0 ? "amber" : "navy"} testid="kpi-fails-24h" />
        <Kpi icon={KeyRound} label="Emails distintos (24 h)" value={stats.distinct_emails_24h ?? 0} tone="navy" testid="kpi-distinct-emails" />
        <Kpi icon={stats.alert ? ShieldAlert : ShieldCheck} label="Estado" value={stats.alert ? "Alerta" : "Normal"} tone={stats.alert ? "red" : "green"} testid="kpi-alert" />
      </div>

      {/* Grafico horario de tentativas (24h) */}
      <div className="mb-8 bg-white border border-tint rounded-xl p-5" data-testid="security-hourly-chart">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={16} className="text-navy" />
          <h3 className="font-bold text-navy tracking-tight text-sm">Tentativas por hora (24h)</h3>
        </div>
        {hourly.length === 0 ? (
          <div className="text-sm text-muted2 py-4">Sem dados nas ultimas 24h.</div>
        ) : (() => {
          const maxV = Math.max(...hourly.map((b) => b.failures + b.successes), 1);
          return (
            <div className="flex items-end gap-1.5 h-28">
              {hourly.map((b) => {
                const total = b.failures + b.successes;
                const hTotal = Math.max(2, Math.round((total / maxV) * 100));
                const failFrac = total > 0 ? b.failures / total : 0;
                return (
                  <div key={b.hour} className="flex-1 flex flex-col items-center justify-end gap-1"
                    title={`${b.hour.slice(11)}h - ${b.failures} falhas / ${b.successes} sucessos`}>
                    <div className="w-full rounded-t overflow-hidden flex flex-col justify-end" style={{ height: `${hTotal}px` }}>
                      <div className="bg-red-400 w-full" style={{ height: `${Math.round(failFrac * 100)}%` }} />
                      <div className="bg-emerald-400 w-full" style={{ height: `${Math.round((1 - failFrac) * 100)}%` }} />
                    </div>
                    <span className="text-[9px] text-slate-400">{b.hour.slice(11)}</span>
                  </div>
                );
              })}
            </div>
          );
        })()}
        <div className="flex items-center gap-4 mt-3 text-[11px] text-muted2">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-400" /> Falhas</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400" /> Sucessos</span>
        </div>
      </div>

      {/* Mapa de ameacas - Top paises */}
      <div className="mb-8 bg-white border border-tint rounded-xl p-5" data-testid="security-threat-map">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-navy" />
            <h3 className="font-bold text-navy tracking-tight text-sm">Mapa de ameacas - Origem por pais</h3>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1" data-testid="geo-view-toggle">
              {[{ k: "map", label: "Mapa" }, { k: "bars", label: "Barras" }].map((v) => (
                <button key={v.k} onClick={() => setGeoView(v.k)} data-testid={`geo-view-${v.k}`}
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
                    geoView === v.k ? "bg-blue text-white" : "bg-tint text-navy hover:bg-slate-200"}`}>
                  {v.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              {[{ k: "failures", label: "Falhas" }, { k: "successes", label: "Sucessos" }].map((v) => (
                <button key={v.k} onClick={() => setGeoMetric(v.k)} data-testid={`geo-metric-${v.k}`}
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
                    geoMetric === v.k ? (v.k === "failures" ? "bg-red-500 text-white" : "bg-emerald-600 text-white") : "bg-tint text-navy hover:bg-slate-200"}`}>
                  {v.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              {[24, 168].map((h) => (
                <button key={h} onClick={() => setGeoHours(h)} data-testid={`geo-window-${h}`}
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
                    geoHours === h ? "bg-navy text-white" : "bg-tint text-navy hover:bg-slate-200"}`}>
                  {h === 24 ? "24h" : "7d"}
                </button>
              ))}
            </div>
          </div>
        </div>
        {!geo ? (
          <div className="text-sm text-muted2 py-4">A carregar...</div>
        ) : geo.buckets.length === 0 ? (
          <div className="text-sm text-muted2 py-4">Sem dados na janela selecionada.</div>
        ) : (
          <>
            <div className="flex items-center gap-6 mb-3 text-xs text-navy">
              <span>Total falhas: <b className="text-red-600">{geo.totals.failures}</b></span>
              <span>Total sucessos: <b className="text-emerald-600">{geo.totals.successes}</b></span>
            </div>
            {geoView === "map" ? (
              <ThreatMap buckets={geo.buckets} metric={geoMetric} />
            ) : (() => {
              const maxFail = Math.max(...geo.buckets.map((b) => b.failures + b.successes), 1);
              return (
                <div className="space-y-1.5">
                  {geo.buckets.slice(0, 15).map((b, i) => {
                    const total = b.failures + b.successes;
                    const w = Math.round((total / maxFail) * 100);
                    const failFrac = total > 0 ? b.failures / total : 0;
                    return (
                      <div key={i} data-testid={`geo-row-${i}`} className="flex items-center gap-3">
                        <span className="w-40 shrink-0 flex items-center gap-2 text-sm text-navy truncate">
                          <span className="text-base">{flagEmoji(b.country_code)}</span>
                          <span className="truncate" title={b.country}>{b.country || <span className="text-slate-400 italic">(nao resolvido)</span>}</span>
                          {b.country_code && <span className="text-[10px] font-mono text-muted2">{b.country_code}</span>}
                        </span>
                        <div className="flex-1 h-5 rounded bg-slate-100 relative overflow-hidden">
                          <div className="absolute inset-y-0 left-0 flex" style={{ width: `${w}%` }}>
                            <div className="bg-red-400 h-full" style={{ width: `${failFrac * 100}%` }} />
                            <div className="bg-emerald-400 h-full" style={{ width: `${(1 - failFrac) * 100}%` }} />
                          </div>
                        </div>
                        <span className="w-16 text-right text-xs text-navy font-semibold">
                          <span className="text-red-600">{b.failures}</span> / <span className="text-emerald-600">{b.successes}</span>
                        </span>
                        <span className="w-14 text-right text-[10px] text-muted2">{b.distinct_ips} IP</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <div className="text-[10px] text-slate-400 mt-3">Resolucao de pais atraves da cache local + ip-api.com. IPs privados aparecem como "Rede interna".</div>
          </>
        )}
      </div>

      {stats.alert && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="mb-6 flex items-start gap-3 border border-red-200 bg-red-50 rounded-lg p-4" data-testid="security-alert-banner">
          <ShieldAlert className="text-red-600 mt-0.5 shrink-0" size={20} />
          <div className="text-sm text-red-700">
            <b>Pico de falhas de login nos ultimos 15 minutos.</b> Pode indicar uma tentativa de ataque de forca bruta.
            Verifique os IPs abaixo e considere rodar o segredo JWT.
          </div>
        </motion.div>
      )}

      {/* Filtros + desbloqueio manual */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap" data-testid="security-filters">
        <div className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`security-filter-${f.key}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                filter === f.key ? "bg-navy text-white" : "bg-tint text-navy hover:bg-slate-200"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={() => setUnblock({ email: "", ip: "", motivo: "" })} data-testid="security-open-unblock"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
          <Unlock size={14} /> Desbloquear acesso
        </button>
      </div>

      {/* Tabela de tentativas */}
      <div className="bg-white border border-tint rounded-xl overflow-hidden" data-testid="security-attempts-table">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-tint bg-slate-50">
          <div className="text-[11px] text-muted2">
            {attemptsTotal > 0
              ? <>A mostrar <b className="text-navy">{attemptsFrom}-{attemptsTo}</b> de <b className="text-navy">{attemptsTotal}</b> tentativas</>
              : "Sem tentativas registadas"}
          </div>
          <div className="flex items-center gap-1" data-testid="attempts-pager">
            <button
              onClick={() => attemptsPage > 0 && load(attemptsPage - 1)}
              disabled={attemptsPage <= 0 || loading}
              data-testid="attempts-pager-prev"
              className={`text-[11px] px-2 py-1 rounded border ${attemptsPage > 0 && !loading ? "border-tint text-navy hover:bg-slate-100" : "border-tint text-slate-300 cursor-not-allowed"}`}>
              Anteriores
            </button>
            <span className="text-[11px] text-slate-500 px-1.5">
              {attemptsMaxPage === 0 ? "1" : `${attemptsPage + 1} / ${attemptsMaxPage + 1}`}
            </span>
            <button
              onClick={() => attemptsPage < attemptsMaxPage && load(attemptsPage + 1)}
              disabled={attemptsPage >= attemptsMaxPage || loading}
              data-testid="attempts-pager-next"
              className={`text-[11px] px-2 py-1 rounded border ${attemptsPage < attemptsMaxPage && !loading ? "border-tint text-navy hover:bg-slate-100" : "border-tint text-slate-300 cursor-not-allowed"}`}>
              Seguintes
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-muted2 text-[11px] uppercase tracking-wide">
              <th className="text-left px-4 py-2.5 font-semibold">Estado</th>
              <th className="text-left px-4 py-2.5 font-semibold">Email</th>
              <th className="text-left px-4 py-2.5 font-semibold">IP</th>
              <th className="text-left px-4 py-2.5 font-semibold">Pais</th>
              <th className="text-left px-4 py-2.5 font-semibold">Data</th>
              <th className="text-left px-4 py-2.5 font-semibold hidden md:table-cell">Cliente</th>
              <th className="text-right px-4 py-2.5 font-semibold">Acao</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted2">A carregar...</td></tr>
            )}
            {!loading && attempts.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted2">Sem tentativas registadas.</td></tr>
            )}
            {!loading && attempts.map((a, i) => (
              <tr key={i} data-testid={`security-attempt-row-${i}`} className="border-t border-tint hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  {a.success ? (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wide font-semibold">
                      <CheckCircle2 size={11} /> Sucesso
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 uppercase tracking-wide font-semibold">
                      <XCircle size={11} /> Falha
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-navy font-medium">{a.email || "-"}</td>
                <td className="px-4 py-2.5">
                  <div className="font-mono text-[12px] text-navy">{a.ip || <span className="text-slate-400">-</span>}</div>
                  <div className="font-mono text-[10px] text-slate-400" title="hash usado no bloqueio">{a.ip_hash}</div>
                </td>
                <td className="px-4 py-2.5 text-sm text-navy">
                  {a.country ? (
                    <span className="inline-flex items-center gap-1">
                      <span>{flagEmoji(a.country_code)}</span>
                      <span className="truncate max-w-[120px]" title={a.country}>{a.country}</span>
                    </span>
                  ) : <span className="text-slate-400">-</span>}
                </td>
                <td className="px-4 py-2.5 text-muted2">{a.created_at ? new Date(a.created_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "medium" }) : "-"}</td>
                <td className="px-4 py-2.5 text-muted2 text-xs truncate max-w-[240px] hidden md:table-cell" title={a.user_agent}>{a.user_agent || "-"}</td>
                <td className="px-4 py-2.5 text-right">
                  {!a.success && (
                    <button data-testid={`security-unblock-row-${i}`}
                      onClick={() => setUnblock({ email: a.email || "", ip: a.ip || "", motivo: "" })}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 underline">
                      <Unlock size={12} /> Desbloquear
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de desbloqueio */}
      {unblock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="security-unblock-modal"
          {...overlayCloseHandlers(() => !unblocking && setUnblock(null))}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Unlock size={18} className="text-emerald-600" />
              <h3 className="font-bold text-navy tracking-tight">Desbloquear acesso</h3>
            </div>
            <p className="text-xs text-muted2 mb-4">Remove as tentativas falhadas do email e/ou IP indicados, repondo o acesso de imediato. Verifique o IP real antes de desbloquear.</p>
            <label className="block text-xs font-semibold text-navy mb-1">Email</label>
            <input data-testid="unblock-email" value={unblock.email} onChange={(e) => setUnblock({ ...unblock, email: e.target.value })}
              placeholder="email@dominio.pt" className="w-full mb-3 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            <label className="block text-xs font-semibold text-navy mb-1">IP</label>
            <input data-testid="unblock-ip" value={unblock.ip} onChange={(e) => setUnblock({ ...unblock, ip: e.target.value })}
              placeholder="ex: 203.0.113.7" className="w-full mb-3 px-3 py-2 text-sm font-mono border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            <label className="block text-xs font-semibold text-navy mb-1">Motivo (auditoria)</label>
            <input data-testid="unblock-motivo" value={unblock.motivo} onChange={(e) => setUnblock({ ...unblock, motivo: e.target.value })}
              placeholder="ex: confirmado com o cliente" className="w-full mb-4 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setUnblock(null)} disabled={unblocking} data-testid="unblock-cancel"
                className="px-3 py-2 rounded-lg text-sm font-semibold text-navy bg-tint hover:bg-slate-200">Cancelar</button>
              <button onClick={doUnblock} disabled={unblocking} data-testid="unblock-confirm"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
                <Unlock size={14} className={unblocking ? "animate-pulse" : ""} /> Desbloquear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Telegram + Allowlist + Country anomaly */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Telegram (read-only - editar em Definicoes > Canais) */}
        <div className="bg-white border border-tint rounded-xl p-5" data-testid="security-telegram-card">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Send size={16} className="text-navy" />
              <h3 className="font-bold text-navy tracking-tight">Alertas por Telegram</h3>
            </div>
            {tg?.configured && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wide font-semibold ${tg.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                {tg.enabled ? "Ativo" : "Inativo"}{tg.bot_username ? ` - @${tg.bot_username}` : ""}
              </span>
            )}
          </div>
          <p className="text-xs text-muted2 mb-3">
            A configuracao e monitorizacao do Telegram foi centralizada em <b>Definicoes &gt; Canais &gt; Telegram</b>.
            Aqui apenas ve o estado. Todas as fontes de alerta (seguranca, handoffs, conectores) usam esta config.
          </p>
          <div className="space-y-2 text-sm mb-3" data-testid="security-tg-readonly">
            <div className="flex items-center justify-between">
              <span className="text-muted2 text-xs">Estado</span>
              <span className="text-navy font-semibold text-xs">{tg?.configured ? (tg?.enabled ? "Ativo" : "Configurado (desligado)") : "Nao configurado"}</span>
            </div>
            {tg?.bot_username && (
              <div className="flex items-center justify-between">
                <span className="text-muted2 text-xs">Bot</span>
                <span className="text-navy font-mono text-xs">@{tg.bot_username}</span>
              </div>
            )}
            {tg?.chat_id_hint && (
              <div className="flex items-center justify-between">
                <span className="text-muted2 text-xs">Chat</span>
                <span className="text-navy font-mono text-xs">{tg.chat_id_hint}</span>
              </div>
            )}
          </div>
          <a href="/app/definicoes" data-testid="tg-open-canais"
             className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-blue hover:bg-[var(--blue-hover)]">
            <Send size={14} /> Editar em Canais &gt; Telegram
          </a>
        </div>

        {/* Allowlist */}
        <div className="bg-white border border-tint rounded-xl p-5" data-testid="security-allowlist-card">
          <div className="flex items-center gap-2 mb-1">
            <Globe size={16} className="text-navy" />
            <h3 className="font-bold text-navy tracking-tight">IPs de confianca</h3>
          </div>
          <p className="text-xs text-muted2 mb-3">
            IPs aqui listados (ex: escritorios) nunca sao bloqueados pela dimensao IP nem geram alertas de pico. O limite por email mantem-se.
          </p>
          <div className="flex gap-2 mb-3">
            <input data-testid="allow-ip" value={newIp.ip} onChange={(e) => setNewIp({ ...newIp, ip: e.target.value })}
              placeholder="ex: 88.1.2.3" className="w-40 px-3 py-2 text-sm font-mono border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            <input data-testid="allow-nota" value={newIp.nota} onChange={(e) => setNewIp({ ...newIp, nota: e.target.value })}
              placeholder="nota (ex: escritorio Lisboa)" className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            <button onClick={addAllow} data-testid="allow-add"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700"><Plus size={14} /></button>
          </div>
          {allowlist.length === 0 ? (
            <div className="text-sm text-muted2 py-2">Sem IPs de confianca.</div>
          ) : (
            <ul className="space-y-1.5" data-testid="allow-list">
              {allowlist.map((x) => (
                <li key={x.ip} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-mono text-[13px] text-navy">{x.ip}</div>
                    {x.nota && <div className="text-[11px] text-muted2 truncate">{x.nota}</div>}
                  </div>
                  <button onClick={() => delAllow(x.ip)} data-testid={`allow-del-${x.ip}`}
                    className="p-1.5 rounded text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Country anomaly */}
        <div className="bg-white border border-tint rounded-xl p-5" data-testid="security-country-card">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-navy" />
              <h3 className="font-bold text-navy tracking-tight">Login de pais invulgar</h3>
            </div>
            <label className="inline-flex items-center gap-2 text-[11px] text-navy font-semibold cursor-pointer" title="Ativar alertas">
              <input type="checkbox" data-testid="country-enabled" checked={!!country?.enabled}
                onChange={(e) => toggleCountry(e.target.checked)} className="w-4 h-4" />
              {country?.enabled ? "Ativo" : "Inativo"}
            </label>
          </div>
          <p className="text-xs text-muted2 mb-3">
            Ao entrar de um pais fora da baseline (30d de sucessos) ou da whitelist, um alerta Telegram e disparado.
          </p>
          <label className="block text-xs font-semibold text-navy mb-1">Paises sempre autorizados (whitelist)</label>
          <div className="flex gap-2 mb-2">
            <input data-testid="country-new" value={countryNew} maxLength={2}
              onChange={(e) => setCountryNew(e.target.value.toUpperCase())}
              placeholder="ex: PT" className="w-20 px-3 py-2 text-sm font-mono border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            <button onClick={addCountry} data-testid="country-add"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700"><Plus size={14} /> Adicionar</button>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="country-list">
            {(country?.allowed_countries || []).length === 0 && (
              <span className="text-[11px] text-muted2 italic">Sem paises adicionais (so a baseline conta).</span>
            )}
            {(country?.allowed_countries || []).map((c) => (
              <span key={c} className="inline-flex items-center gap-1 bg-slate-100 rounded-full pl-2 pr-1 py-0.5 text-[11px] font-mono text-navy">
                <span>{flagEmoji(c)}</span> {c}
                <button onClick={() => removeCountry(c)} data-testid={`country-del-${c}`}
                  className="p-0.5 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"><X size={10} /></button>
              </span>
            ))}
          </div>

          {/* Regra de auto-bloqueio */}
          <div className="mt-2 border-t border-tint pt-3" data-testid="country-block-rule">
            <label className="flex items-center gap-2 text-sm text-navy font-semibold mb-2 cursor-pointer">
              <input type="checkbox" data-testid="country-block-enabled"
                checked={!!country?.block_unauthorized}
                onChange={(e) => toggleBlock(e.target.checked)} className="w-4 h-4" />
              <Lock size={13} /> Bloquear IPs de paises nao-autorizados
            </label>
            <div className="grid grid-cols-2 gap-2 mb-1">
              <div>
                <label className="block text-[10px] font-semibold text-muted2 mb-0.5 uppercase tracking-wide">Falhas antes de bloquear</label>
                <input type="number" min={1} max={20} disabled={!country?.block_unauthorized} data-testid="country-block-fails"
                  value={country?.block_after_fails ?? 3}
                  onChange={(e) => setCountry({ ...country, block_after_fails: Number(e.target.value) })}
                  onBlur={(e) => saveCountry({ block_after_fails: Number(e.target.value) })}
                  className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-muted2 mb-0.5 uppercase tracking-wide">Duracao do bloqueio (min)</label>
                <input type="number" min={5} max={1440} disabled={!country?.block_unauthorized} data-testid="country-block-duration"
                  value={country?.block_duration_min ?? 60}
                  onChange={(e) => setCountry({ ...country, block_duration_min: Number(e.target.value) })}
                  onBlur={(e) => saveCountry({ block_duration_min: Number(e.target.value) })}
                  className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400" />
              </div>
            </div>
            <p className="text-[11px] text-muted2">
              Ativa uma segunda linha de defesa: apos {country?.block_after_fails ?? 3} falhas de um IP cujo pais nao esta na whitelist acima, o acesso e recusado (429) durante {country?.block_duration_min ?? 60} min. O limite normal por email continua a funcionar.
            </p>
          </div>
          {countryEvents.length > 0 && (
            <>
              <div className="text-[11px] font-semibold text-navy mb-1 mt-3">Eventos recentes</div>
              <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="country-events">
                {countryEvents.slice(0, 10).map((ev, i) => (
                  <li key={i} className="text-[11px] flex items-center justify-between gap-2 bg-slate-50 rounded px-2 py-1">
                    <span className="flex items-center gap-1 min-w-0">
                      <span>{flagEmoji(ev.country_code)}</span>
                      <span className="truncate text-navy">{ev.email}</span>
                    </span>
                    <span className="text-muted2 shrink-0 font-mono">{ev.country_code}</span>
                    <span className="text-slate-400 shrink-0 text-[10px]">{ev.created_at ? new Date(ev.created_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* Historico de auto-bloqueios */}
      <div className="mt-4 bg-white border border-tint rounded-xl p-5" data-testid="security-blocks-card">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Lock size={16} className="text-red-600" />
            <h3 className="font-bold text-navy tracking-tight">Historico de auto-bloqueios (24h)</h3>
          </div>
          <span className="text-[11px] text-muted2">
            Total de eventos: <b className="text-navy">{blocks?.total_events ?? 0}</b>
            {blocks?.blocks?.length ? ` - ${blocks.blocks.length} IP(s) unico(s)` : ""}
          </span>
        </div>
        {(!blocks || blocks.blocks.length === 0) ? (
          <div className="text-sm text-muted2 py-4">Sem bloqueios automaticos nas ultimas 24 horas.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted2 text-[10px] uppercase tracking-wide border-b border-tint">
                  <th className="text-left px-2 py-2 font-semibold">IP</th>
                  <th className="text-left px-2 py-2 font-semibold">Pais</th>
                  <th className="text-right px-2 py-2 font-semibold">Falhas</th>
                  <th className="text-right px-2 py-2 font-semibold">Bloqueio</th>
                  <th className="text-right px-2 py-2 font-semibold">Ocorrencias</th>
                  <th className="text-right px-2 py-2 font-semibold">Ultimo</th>
                  <th className="text-center px-2 py-2 font-semibold">Alerta</th>
                  <th className="text-right px-2 py-2 font-semibold">Acao</th>
                </tr>
              </thead>
              <tbody>
                {blocks.blocks.map((b, i) => (
                  <tr key={b.ip} data-testid={`block-row-${i}`} className="border-t border-tint hover:bg-slate-50">
                    <td className="px-2 py-2 font-mono text-[12px] text-navy">{b.ip}</td>
                    <td className="px-2 py-2">
                      <span className="inline-flex items-center gap-1 text-navy">
                        <span>{flagEmoji(b.country_code)}</span>
                        <span className="truncate max-w-[140px]" title={b.country}>{b.country || "-"}</span>
                        {b.country_code && <span className="text-[10px] font-mono text-muted2">{b.country_code}</span>}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right text-navy font-semibold">{b.last_fails}</td>
                    <td className="px-2 py-2 text-right text-red-600 font-mono text-[12px]">
                      {Math.floor((b.last_retry_seconds || 0) / 60)}m {(b.last_retry_seconds || 0) % 60}s
                    </td>
                    <td className="px-2 py-2 text-right text-navy">{b.count}</td>
                    <td className="px-2 py-2 text-right text-muted2 text-[11px]">{b.last_at ? new Date(b.last_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "-"}</td>
                    <td className="px-2 py-2 text-center">
                      {b.alerted ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold" title="Alerta Telegram enviado">
                          <Send size={10} /> TG
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400" title="Telegram nao configurado ou dentro do cooldown">-</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button data-testid={`block-unblock-${i}`}
                        onClick={() => setUnblock({ email: "", ip: b.ip, motivo: `desbloqueio manual apos auto-block (${b.country_code || "?"})` })}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 underline">
                        <Unlock size={11} /> Desbloquear
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[10px] text-slate-400 mt-2">Eventos consolidados por IP nas ultimas 24 horas. TTL da colecao: 30 dias.</div>
          </div>
        )}
      </div>

      {/* Segredo JWT */}
      <div className="mt-8 bg-white border border-tint rounded-xl p-5" data-testid="security-jwt-card">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound size={16} className="text-navy" />
          <h3 className="font-bold text-navy tracking-tight">Segredo de sessao (JWT)</h3>
        </div>
        <div className="text-sm text-body space-y-1 mb-4">
          <div className="flex justify-between max-w-md"><span className="text-muted2">Configurado no ambiente</span><b>{secStatus?.jwt_env_configurado ? "Sim" : "Nao"}</b></div>
          <div className="flex justify-between max-w-md"><span className="text-muted2">Override em base de dados</span><b>{secStatus?.jwt_override_em_db ? "Sim" : "Nao"}</b></div>
          {secStatus?.rotated_at && (
            <div className="flex justify-between max-w-md"><span className="text-muted2">Ultima rotacao</span><b>{new Date(secStatus.rotated_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })} ({secStatus.rotated_by || "-"})</b></div>
          )}
        </div>
        <button onClick={rotate} disabled={rotating} data-testid="security-rotate-jwt"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
          <RefreshCw size={14} className={rotating ? "animate-spin" : ""} /> Rodar segredo JWT
        </button>
        <p className="text-xs text-muted2 mt-2">Invalida todas as sessoes ativas. Use em caso de suspeita de compromisso.</p>
      </div>
    </div>
  );
}
